import { I18nThemed } from 'interfaces/mixins';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { Store } from '@dojo/framework/stores/Store';
import { v, w } from '@dojo/framework/core/vdom';
import { WidgetProperties, DNode } from '@dojo/framework/core/interfaces';
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';

import { logQuiz } from 'logger';
import { Queue } from 'collections';
import {
	QuizMeta,
	QuizRPC,
	InfoRPC,
	ProgressionSvc,
	QuestionType,
	IResult,
	StartQuiz,
	Question,
	QuestionTypeProperties
} from '../interfaces';
import { setQuestion, setInreview, setHasQueue, incrCursor } from './state';
import timesync from 'timesync';
import { QueueLoader, allqueues } from './queueloader';

import css from '../css/questionfeedback.m.css';
import { recordProgress } from './xapi';

export interface QuestionQueueProperties {
	quizstore: Store<QuizMeta>;
	svc: ProgressionSvc;
	finalize: () => void;
	startquiz: StartQuiz;
	quiz: QuizRPC;
	info: InfoRPC;
	question: Question | undefined;
	instancekey: string;
}

@theme(css)
export class QuestionQueue extends I18nThemed<QuestionQueueProperties> {
	private instanceId: string | undefined;
	private originalId: string | undefined;
	private detaching: boolean = false;
	private _inited: boolean = false;

	public get loader(): QueueLoader | undefined {
		const { instanceId, detaching } = this;
		const {
			svc,
			quizstore,
			info: { numberofquestions }
		} = this.properties;
		if (instanceId !== undefined) {
			if (allqueues.has(instanceId)) {
				return allqueues.get(instanceId);
			}
			if (!detaching) {
				const qloader = new QueueLoader({ svc, numberofquestions, instanceId, useQueue: true, quizstore });
				allqueues.set(instanceId, qloader);
				return qloader;
			}
		}
		return undefined;
	}

	public get queue(): Queue<Question> | undefined {
		const { loader } = this;
		return loader && (loader.queue as Queue<Question>);
	}

	public stopLoading(): void {
		logQuiz.debug(() => `QuestionQueue#stopLoading for ${this.instanceId}`);
		const qloader = this.loader;
		if (qloader === undefined || qloader.stoppedloading) {
			return;
		}
		qloader.stopLoading();
	}

	protected has_next_question(): boolean {
		const { loader } = this;
		return Boolean(loader && !loader.queue.isEmpty());
	}

	protected next_question(): void {
		const { queue } = this;
		const { quizstore, quiz, info } = this.properties;
		if (queue) {
			const question = queue.dequeue();
			if (question) {
				incrCursor(quizstore);
				question.starttime = timesync.now().toJSON();
				setQuestion(quizstore)({ question });
				recordProgress(quiz, info, question.cursor);
			}
		}
	}

	protected async finish_quiz(): Promise<void> {
		const { finalize, svc, quizstore } = this.properties;
		setQuestion(quizstore)({ question: undefined });
		setInreview(quizstore)({ inreview: false });
		await svc.finish_quiz();
		finalize();
	}

	protected update_results(results: IResult[]): Promise<any> {
		const {
			info: { qrid },
			quiz: { id },
			svc
		} = this.properties;

		results.forEach((result) => {
			result.qrlocator.qrid = qrid;
			result.qrlocator.quiz = id;
		});
		return svc.update_results({ results });
	}

	protected onDetach(): void {
		logQuiz.debug(() => `QuestionQueue#onDetach for ${this.originalId}`);
		this.detaching = true;
		const qloader = this.loader;
		const { instanceId } = this;
		qloader && qloader.stopLoading();
		instanceId && allqueues.delete(instanceId);
	}

	protected onAttach(): void {
		if (allqueues.size > 0) {
			logQuiz.debug(() => `already have QuestionQueue ${Array.from(allqueues.keys()).join(', ')}`);
			this.originalId = this.instanceId;
			this.instanceId = '--bad--';
			this.detaching = true;
			return;
		}

		const { quizstore } = this.properties;
		setHasQueue(quizstore)({ hasqueue: true });

		const { startquiz, instancekey } = this.properties;
		if (this.instanceId && this.instanceId !== instancekey) {
			logQuiz.debug(() => `QuestionQueue#onAttach for defunct ${this.originalId}`);
			return;
		}
		this.instanceId = instancekey;
		this.originalId = instancekey;
		logQuiz.debug(() => `QuestionQueue#onAttach for ${this.instanceId}`);

		const { loader } = this;
		console.log(`QuestionQueue#loader =`, loader);
		if (loader) {
			loader.load_questions(startquiz);
		}
	}

	protected render(): DNode {
		const { instancekey, svc, quizstore } = this.properties;
		const { detaching, instanceId } = this;
		if (detaching || (instanceId && instanceId !== instancekey)) {
			logQuiz.debug(() => `QuestionQueue#render defunct instance ${this.originalId}`);
			return null;
		}

		const { question } = this.properties;
		if (!question) {
			return v('div', { key: 'spinner', classes: [this.theme(css.loading)] }, [
				v('i', { classes: ['fa', 'fa-spinner', 'fa-spin', 'fa-lg'] })
			]);
		}
		const { questiontype } = question;
		const key = `${questiontype}-${instancekey}`;
		return w<QuestionType>(questiontype, {
			key,
			progressionSvc: svc,
			quizstore,
			hasNextQuestion: this.has_next_question,
			onNextQuestion: this.next_question,
			onFinish: this.finish_quiz,
			updater: this.update_results,
			// not used here, but required by the interface
			loaded: false,
			filterOn: () => undefined,
			onReview: () => undefined,
			getQuestions: () => []
		} as QuestionTypeProperties & WidgetProperties);
	}

	@beforeProperties()
	protected stateProperties(
		properties: QuestionQueueProperties & WidgetProperties
	): QuestionQueueProperties & WidgetProperties {
		const { quizstore } = properties;
		const { get, path } = quizstore;

		if (!this._inited) {
			const paths = [['quiz'], ['info'], ['question'], ['instancekey']];
			const invalidatePaths = paths.map((path: any) => quizstore.path(path.join('/')));
			const handle = quizstore.onChange(invalidatePaths, () => this.invalidate());
			this.own({
				destroy: () => {
					handle.remove();
				}
			});
			this._inited = true;
		}

		properties.quiz = get(path('quiz'));
		properties.info = get(path('info'));
		properties.question = get(path('question'));
		properties.instancekey = get(path('instancekey'));
		return properties;
	}
}

export default QuestionQueue;
